import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';
import { Menu, X, Sparkles } from 'lucide-react';
import styled from 'styled-components';

const StyledNav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${props => props.theme.zIndex.fixed};
  transition: all 0.5s ease;
  padding: ${props => props.isScrolled ? props.theme.spacing.md : props.theme.spacing['2xl']} 0;
  background: ${props => props.isScrolled 
    ? 'rgba(255, 255, 255, 0.95)' 
    : 'transparent'};
  backdrop-filter: ${props => props.isScrolled ? 'blur(12px)' : 'none'};
  box-shadow: ${props => props.isScrolled ? props.theme.shadows.elegant : 'none'};
`;

const NavContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing['2xl']};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.default};
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
    transition: ${props => props.theme.transitions.default};
    color: ${props => props.isScrolled 
      ? props.theme.colors.primary.main 
      : props.theme.colors.primary.foreground};
  }
  
  span {
    font-family: ${props => props.theme.typography.fontFamily.display};
    font-size: ${props => props.theme.typography.fontSize.xl};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    transition: ${props => props.theme.transitions.default};
    color: ${props => props.isScrolled 
      ? props.theme.colors.primary.main 
      : props.theme.colors.primary.foreground};
  }
`;

const DesktopNav = styled.div`
  display: none;
  align-items: center;
  gap: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
  }
`;

const NavLink = styled.a`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: ${props => props.theme.transitions.default};
  color: ${props => props.isScrolled 
    ? props.theme.colors.text.primary 
    : props.theme.colors.primary.foreground};
  
  &:hover {
    opacity: 0.7;
  }
`;

const MobileMenuButton = styled.button`
  display: block;
  padding: ${props => props.theme.spacing.sm};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: ${props => props.isScrolled 
      ? props.theme.colors.text.primary 
      : props.theme.colors.primary.foreground};
  }
`;

const MobileMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  box-shadow: ${props => props.theme.shadows.elegant};
  animation: fadeIn 0.3s ease-out;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing['2xl']};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const MobileNavLink = styled.a`
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  padding: ${props => props.theme.spacing.sm} 0;
`;

const MobileMenuDivider = styled.div`
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border.main};
`;

const Navigation = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#hero', label: t('nav.home') },
    { href: '#featured', label: t('nav.featured') },
    { href: '#faq', label: t('nav.faq') },
  ];

  return (
    <StyledNav isScrolled={isScrolled}>
      <NavContainer>
        <LogoLink href="#hero" isScrolled={isScrolled}>
          <Sparkles />
          <span>Blue Dreams</span>
        </LogoLink>

        {/* Desktop Navigation */}
        <DesktopNav>
          {navLinks.map((link) => (
            <NavLink
              key={link.href}
              href={link.href}
              isScrolled={isScrolled}
            >
              {link.label}
            </NavLink>
          ))}
          <LanguageToggle />
        </DesktopNav>

        {/* Mobile Menu Button */}
        <MobileMenuButton
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
          isScrolled={isScrolled}
        >
          {isMobileMenuOpen ? <X /> : <Menu />}
        </MobileMenuButton>
      </NavContainer>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <MobileMenu>
          <MobileMenuContent>
            {navLinks.map((link) => (
              <MobileNavLink
                key={link.href}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </MobileNavLink>
            ))}
            <MobileMenuDivider>
              <LanguageToggle />
            </MobileMenuDivider>
          </MobileMenuContent>
        </MobileMenu>
      )}
    </StyledNav>
  );
};

export default Navigation;
